import { useState } from "react";
import { KeyRound, Plus, Settings, UserCog } from "lucide-react";
import type { z } from "zod";
import type {
  checklistItemSchema,
  lineConfigurationSchema,
  measurementSchema,
  ShiftProductionLineName,
} from "../../../shared/shift-production-form/contracts";
import { FormDetail } from "./ManagerFormWorkspace";
import { productionLines, shiftLabels } from "./line-options";
import { useAdminFormWorkspace } from "./useAdminFormWorkspace";
import { DocumentSettingsForm } from "./DocumentSettingsForm";
import { Toast } from "../../shared/components/Toast";
import { useToastNotification } from "../../shared/components/useToastNotification";
import { ApprovalAssignmentsPanel } from "../shift-form-approval/ApprovalAssignmentsPanel";
import { ApprovalMailSchedulePanel } from "../shift-form-approval/ApprovalMailSchedulePanel";

type Configuration = z.infer<typeof lineConfigurationSchema>;
type MeasurementKey = z.infer<typeof measurementSchema>["key"];
type ChecklistKey = z.infer<typeof checklistItemSchema>["key"];
const measurements: Array<[MeasurementKey, string]> = [
  ["THEORETICAL_LINE_SPEED", "Teorik hız"],
  ["ACTUAL_LINE_SPEED", "Gerçekleşen hız"],
  ["MACHINE_TEMPERATURE", "Makine sıcaklığı"],
  ["UNIT_WEIGHT", "Tek ürün ağırlığı"],
  ["PRODUCT_MOISTURE", "Ürün nemi"],
  ["AMBIENT_TEMPERATURE", "Ortam sıcaklığı"],
  ["AMBIENT_HUMIDITY", "Ortam nemi"],
];
const checks: Array<[ChecklistKey, string]> = [
  ["TANK_AND_PIPING", "Tank ve boru"],
  ["MOVING_PARTS", "Hareketli aksam"],
  ["GUARDS_SENSORS_SWITCHES", "Muhafaza/sensör/switch"],
  ["MOLD", "Kalıp"],
  ["REEL_PACKAGING_LABEL_BOX", "Bobin/ambalaj/etiket/koli"],
];

export function AdminFormWorkspace() {
  const workspace = useAdminFormWorkspace();
  const toast = useToastNotification(
    workspace.error ?? workspace.documentSettingsNotice,
    workspace.error ? "error" : "success",
  );
  const [section, setSection] = useState<"operators" | "lines" | "approvals" | "document" | "operations">(
    "operators",
  );
  return (
    <section className="shift-form-workspace">
      <div className="shift-form-workspace-heading">
        <div>
          <h2>Sistem yönetimi</h2>
          <p>Operatör erişimi, hat onay zinciri ve gerekçeli istisnalar.</p>
        </div>
      </div>
      <nav className="shift-manager-tabs">
        <button
          type="button"
          className={section === "operators" ? "is-current" : ""}
          onClick={() => { workspace.clearPin(); setSection("operators"); }}
        >
          <UserCog size={16} />
          Operatörler
        </button>
        <button
          type="button"
          className={section === "lines" ? "is-current" : ""}
          onClick={() => { workspace.clearPin(); setSection("lines"); }}
        >
          <Settings size={16} />
          Hat kurulumu
        </button>
        <button
          type="button"
          className={section === "document" ? "is-current" : ""}
          onClick={() => { workspace.clearPin(); setSection("document"); }}
        >
          <Settings size={16} />
          Doküman bilgileri
        </button>
        <button
          type="button"
          className={section === "approvals" ? "is-current" : ""}
          onClick={() => { workspace.clearPin(); setSection("approvals"); }}
        >
          <Settings size={16} />
          Onay atamaları
        </button>
        <button
          type="button"
          className={section === "operations" ? "is-current" : ""}
          onClick={() => { workspace.clearPin(); setSection("operations"); }}
        >
          <KeyRound size={16} />
          Form işlemleri
        </button>
      </nav>
      {workspace.issuedPin ? (
        <div className="shift-pin-result" role="status">
          <strong>{workspace.issuedPin.name} için PIN</strong>
          <code>{workspace.issuedPin.pin}</code>
          <p>
            PIN 30 saniye sonra veya ekran odağı değişince gizlenir.
            Yalnız ilgili operatöre güvenli biçimde iletin.
          </p>
          <button type="button" onClick={workspace.clearPin}>
            Gösterimi kapat
          </button>
        </div>
      ) : null}
      {section === "operators" ? (
        <Operators workspace={workspace} />
      ) : section === "lines" ? (
        <LineSetup workspace={workspace} />
      ) : section === "approvals" ? (
        <><ApprovalMailSchedulePanel /><ApprovalAssignmentsPanel /></>
      ) : section === "document" ? (
        workspace.documentSettings ? (
          <DocumentSettingsForm
            settings={workspace.documentSettings}
            busy={workspace.busy}
            onSave={workspace.saveDocumentSettings}
            onReload={workspace.reloadDocumentSettings}
          />
        ) : (
          <div className="shift-admin-section">
            <p>Doküman bilgileri yüklenemedi.</p>
            <button type="button" disabled={workspace.busy} onClick={() => void workspace.reloadDocumentSettings()}>
              Yeniden dene
            </button>
          </div>
        )
      ) : (
        <AdminOperations workspace={workspace} />
      )}
      {toast.notification ? (
        <Toast
          message={toast.notification.message}
          tone={toast.notification.tone}
          onDismiss={toast.dismiss}
        />
      ) : null}
    </section>
  );
}

function Operators({
  workspace,
}: {
  workspace: ReturnType<typeof useAdminFormWorkspace>;
}) {
  return (
    <div className="shift-admin-section">
      <form
        className="shift-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          const name = String(data.get("name") ?? "").trim();
          if (name) void workspace.addOperator(name);
        }}
      >
        <label className="shift-form-field">
          <span>Yeni operatör adı</span>
          <input name="name" required />
        </label>
        <button
          type="submit"
          className="shift-form-primary"
          disabled={workspace.busy}
        >
          <Plus size={16} />
          Operatör ve rastgele PIN oluştur
        </button>
      </form>
      <table>
        <thead>
          <tr>
            <th>Operatör</th>
            <th>Durum</th>
            <th>PIN tarihi</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {workspace.operators.map((operator) => (
            <tr key={operator.id}>
              <td>{operator.name}</td>
              <td>{operator.active ? "Aktif" : "Pasif"}</td>
              <td>{new Date(operator.pinChangedAt).toLocaleString("tr-TR")}</td>
              <td>
                {operator.pinRevealAvailable ? (
                  <button type="button" disabled={workspace.busy} onClick={() => void workspace.showPin(operator)}>
                    PIN’i göster
                  </button>
                ) : <span>Mevcut PIN korunuyor; görüntülenemiyor.</span>}
                <button
                  type="button"
                  onClick={() => {
                    workspace.clearPin();
                    if (window.confirm(`${operator.name} için mevcut PIN değişecek ve açık oturumları kapanacak. Yeni PIN oluşturulsun mu?`)) void workspace.resetPin(operator.id);
                  }}
                  disabled={workspace.busy}
                >
                  PIN yenile
                </button>
                <button
                  type="button"
                  onClick={() => void workspace.toggleOperator(operator)}
                  disabled={workspace.busy}
                >
                  {operator.active ? "Pasife al" : "Aktifleştir"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {workspace.operators.length === 0 ? (
        <p>Henüz operatör tanımlı değil.</p>
      ) : null}
    </div>
  );
}

function LineSetup({
  workspace,
}: {
  workspace: ReturnType<typeof useAdminFormWorkspace>;
}) {
  const [lineName, setLineName] = useState<ShiftProductionLineName>(
    productionLines[0],
  );
  const configuration = workspace.configurations.find(
    (item) => item.lineName === lineName,
  );
  if (!configuration)
    return (
      <div className="shift-admin-section">
        <label className="shift-form-field">
          <span>Hat</span>
          <select
            value={lineName}
            onChange={(event) =>
              setLineName(event.target.value as ShiftProductionLineName)
            }
          >
            {productionLines.map((line) => (
              <option key={line}>{line}</option>
            ))}
          </select>
        </label>
        <p>Hat yapılandırması henüz oluşturulmamış.</p>
      </div>
    );
  return (
    <div className="shift-admin-section">
      <label className="shift-form-field">
        <span>Hat</span>
        <select
          value={lineName}
          onChange={(event) =>
            setLineName(event.target.value as ShiftProductionLineName)
          }
        >
          {productionLines.map((line) => (
            <option key={line}>{line}</option>
          ))}
        </select>
      </label>
      <LineConfigurationForm
        key={`${configuration.lineName}-${configuration.version}`}
        configuration={configuration}
        busy={workspace.busy}
        onSave={workspace.saveConfiguration}
      />
    </div>
  );
}

function LineConfigurationForm({
  configuration,
  busy,
  onSave,
}: {
  configuration: Configuration;
  busy: boolean;
  onSave: (configuration: Configuration) => Promise<void>;
}) {
  const [draft, setDraft] = useState(configuration);
  const toggle = <T extends string>(values: T[], value: T, checked: boolean) =>
    checked ? [...values, value] : values.filter((item) => item !== value);
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(draft);
      }}
    >
      <p>Onay zinciri ve görüntüleme yetkileri “Onay atamaları” sekmesinden yönetilir.</p>
      <div className="shift-form-grid">
        <label className="shift-form-field">
          <span>Hız birimi</span>
          <input
            value={draft.speedUnit}
            onChange={(event) =>
              setDraft({ ...draft, speedUnit: event.target.value })
            }
          />
        </label>
        <label className="shift-form-field">
          <span>Bobin firesi birimi</span>
          <input
            value={draft.reelWasteUnit}
            onChange={(event) =>
              setDraft({ ...draft, reelWasteUnit: event.target.value })
            }
          />
        </label>
        <label className="shift-form-field">
          <span>Makine sıcaklık noktası</span>
          <input
            value={draft.machineTemperaturePoint ?? ""}
            onChange={(event) =>
              setDraft({
                ...draft,
                machineTemperaturePoint: event.target.value || null,
              })
            }
          />
        </label>
        <label className="shift-form-field">
          <span>Ürün nemi yöntemi</span>
          <input
            value={draft.productMoistureMethod ?? ""}
            onChange={(event) =>
              setDraft({
                ...draft,
                productMoistureMethod: event.target.value || null,
              })
            }
          />
        </label>
      </div>
      <fieldset>
        <legend>Uygulanmayan ölçümler</legend>
        {measurements.map(([key, label]) => (
          <label className="shift-form-choice" key={key}>
            <input
              type="checkbox"
              checked={draft.notApplicableMeasurementKeys.includes(key)}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  notApplicableMeasurementKeys: toggle(
                    draft.notApplicableMeasurementKeys,
                    key,
                    event.target.checked,
                  ),
                })
              }
            />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset>
        <legend>Uygulanmayan kontroller</legend>
        {checks.map(([key, label]) => (
          <label className="shift-form-choice" key={key}>
            <input
              type="checkbox"
              checked={draft.notApplicableChecklistKeys.includes(key)}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  notApplicableChecklistKeys: toggle(
                    draft.notApplicableChecklistKeys,
                    key,
                    event.target.checked,
                  ),
                })
              }
            />
            {label}
          </label>
        ))}
      </fieldset>
      <button className="shift-form-primary" disabled={busy} type="submit">
        Hat yapılandırmasını kaydet
      </button>
    </form>
  );
}

function AdminOperations({
  workspace,
}: {
  workspace: ReturnType<typeof useAdminFormWorkspace>;
}) {
  return (
    <div className="shift-admin-section">
      <HistoricalForm workspace={workspace} />
      <div className="shift-manager-layout shift-admin-operations">
        <div>
          <h3>Formlar</h3>
          <ul className="shift-admin-form-list">
            {workspace.forms.map((form) => (
              <li key={form.id}>
                <button
                  type="button"
                  onClick={() => void workspace.open(form.id)}
                >
                  <strong>{form.formNo}</strong><span>{form.lineName}</span><time dateTime={form.productionDate ?? undefined}>{form.productionDate?.split("-").reverse().join(".")}</time>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {workspace.selected ? (
          <FormDetail
            form={workspace.selected}
            busy={workspace.busy}
            onClose={() => workspace.setSelected(null)}
            adminActions={<AdminActions workspace={workspace} />}
          />
        ) : (
          <p>İşlem yapmak için form seçin.</p>
        )}
      </div>
    </div>
  );
}

function HistoricalForm({
  workspace,
}: {
  workspace: ReturnType<typeof useAdminFormWorkspace>;
}) {
  return (
    <form
      className="shift-admin-history-form"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const operator = workspace.operators.find(
          (item) => item.id === data.get("operator"),
        );
        if (operator)
          void workspace.createHistorical(
            operator.id,
            operator.name,
            String(data.get("line")) as ShiftProductionLineName,
            String(data.get("date")),
            String(data.get("shift")) as "00_08" | "08_16" | "16_00",
            String(data.get("reason")),
          );
      }}
    >
      <h3>Geçmiş tarihli boş form aç</h3>
      <select name="operator" required>
        <option value="">Operatör seçin</option>
        {workspace.operators
          .filter((item) => item.active)
          .map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
      </select>
      <select name="line" required>
        <option value="">Hat seçin</option>
        {productionLines.map((line) => (
          <option key={line}>{line}</option>
        ))}
      </select>
      <input name="date" type="date" required />
      <select name="shift" required>
        {Object.entries(shiftLabels).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <input name="reason" placeholder="Gerekçe" required />
      <button className="shift-form-primary" type="submit">
        Form aç
      </button>
    </form>
  );
}

function AdminActions({
  workspace,
}: {
  workspace: ReturnType<typeof useAdminFormWorkspace>;
}) {
  return (
    <div className="shift-admin-actions">
      <h4>Yönetici işlemleri</h4>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          void workspace.transfer(
            String(data.get("operator")),
            String(data.get("reason")),
          );
        }}
      >
        <select name="operator" required>
          <option value="">Yeni operatör</option>
          {workspace.operators
            .filter((item) => item.active)
            .map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
        </select>
        <input name="reason" required placeholder="Devir gerekçesi" />
        <button type="submit">Devret</button>
      </form>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          void workspace.grant(
            String(data.get("reason")),
            String(data.get("expires")),
          );
        }}
      >
        <input name="expires" type="datetime-local" required />
        <input name="reason" required placeholder="Geç tamamlama gerekçesi" />
        <button type="submit">Geç tamamlama izni ver</button>
      </form>
      {workspace.selected?.status === "COMPLETED" ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void workspace.reopen(
              String(new FormData(event.currentTarget).get("reason")),
            );
          }}
        >
          <input name="reason" required placeholder="Yeniden açma gerekçesi" />
          <button type="submit">Yeni düzeltme sürümü aç</button>
        </form>
      ) : null}
    </div>
  );
}
